import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { PageLayout } from '../layouts/PageLayout';
import { SEOHead } from '../components/seo/SEOHead';
import { Badge } from '../components/ui/Badge';
import { useMarkdown } from '../hooks/useMarkdown';
import { buildArticleSchema, SITE } from '../utils/seo';

export function ArticleDetail() {
  const { slug } = useParams();
  const { frontmatter, content, loading, error } = useMarkdown(`/content/articles/${slug}.md`);

  const schema = frontmatter.title
    ? buildArticleSchema({
        title: frontmatter.title,
        description: frontmatter.description || '',
        date: frontmatter.date || '',
        slug,
      })
    : null;

  return (
    <PageLayout>
      {frontmatter.title && (
        <SEOHead
          title={frontmatter.title}
          description={frontmatter.description || SITE.description}
          canonical={`${SITE.url}/articles/${slug}`}
          ogType="article"
          jsonLd={schema ? [schema] : []}
        />
      )}

      <article className="max-w-2xl">
        {loading && <p className="text-[var(--color-muted)] animate-pulse">Loading…</p>}
        {error && (
          <div>
            <p className="text-[var(--color-muted)]">Article not found.</p>
            <Link to="/articles" className="text-[var(--color-accent)] text-sm hover:underline mt-2 inline-block">
              ← Back to articles
            </Link>
          </div>
        )}

        {!loading && !error && (
          <>
            <header className="mb-8">
              <Link
                to="/articles"
                className="text-xs text-[var(--color-muted)] hover:text-[var(--color-accent)] transition-colors font-mono tracking-wide inline-flex items-center gap-1 mb-4"
                aria-label="Back to articles"
              >
                ← articles
              </Link>
              {frontmatter.date && (
                <time
                  dateTime={frontmatter.date}
                  className="block text-xs text-[var(--color-muted)]/60 font-mono tracking-wide mb-2"
                >
                  {frontmatter.date}
                </time>
              )}
              <h1 className="font-serif text-3xl sm:text-4xl text-[var(--color-text)] leading-tight">
                {frontmatter.title}
              </h1>
              {frontmatter.description && (
                <p className="text-[var(--color-muted)] mt-3 text-base leading-relaxed">
                  {frontmatter.description}
                </p>
              )}
              {Array.isArray(frontmatter.tags) && frontmatter.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {frontmatter.tags.map((tag) => (
                    <Badge key={tag}>{tag}</Badge>
                  ))}
                </div>
              )}
            </header>

            <div className="markdown-body">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
            </div>
          </>
        )}
      </article>
    </PageLayout>
  );
}
